import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;

  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    retryCount: 0
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Only log sensitive details in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    } else {
      // In production, only log essential info
      console.error('Application error:', error.message);
    }
    
    this.setState({ errorInfo });
  }

  private handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1
      }));
    } else {
      // After max retries, redirect to home
      window.location.href = '/';
    }
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleReportError = () => {
    // Create a sanitized error report (no sensitive data)
    const errorReport = {
      message: this.state.error?.message || 'Unknown error',
      timestamp: new Date().toISOString(),
      url: window.location.pathname, // Only pathname, not full URL
      userAgent: navigator.userAgent.substring(0, 100) // Truncated
    };
    
    console.log('Error report generated:', errorReport);
    alert('Fout gerapporteerd. Bedankt voor je feedback!');
  };

  public render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isMaxRetriesReached = this.state.retryCount >= this.maxRetries;

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
          <Card className="w-full max-w-lg shadow-lg">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <CardTitle className="text-xl">
                {isMaxRetriesReached ? 'Persistente fout' : 'Er is iets misgegaan'}
              </CardTitle>
              <CardDescription className="text-base">
                {isMaxRetriesReached 
                  ? 'We kunnen dit probleem niet automatisch oplossen. Probeer later opnieuw.'
                  : 'Er is een onverwachte fout opgetreden. We kunnen proberen dit op te lossen.'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {this.state.retryCount > 0 && (
                <div className="text-center">
                  <p className="text-sm text-orange-600">
                    Poging {this.state.retryCount} van {this.maxRetries}
                  </p>
                </div>
              )}

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="text-left">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2">
                    🔧 Ontwikkelaar details
                  </summary>
                  <div className="text-xs bg-gray-100 p-3 rounded-md font-mono">
                    <div className="mb-2">
                      <strong>Error:</strong> {this.state.error.message}
                    </div>
                    {this.state.error.stack && (
                      <div>
                        <strong>Stack:</strong>
                        <pre className="whitespace-pre-wrap mt-1 text-xs">{this.state.error.stack}</pre>
                      </div>
                    )}
                  </div>
                </details>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {!isMaxRetriesReached ? (
                  <Button onClick={this.handleRetry} className="w-full">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Probeer opnieuw
                  </Button>
                ) : (
                  <Button onClick={this.handleReload} className="w-full">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Herlaad pagina
                  </Button>
                )}
                
                <Button onClick={this.handleGoHome} variant="outline" className="w-full">
                  <Home className="h-4 w-4 mr-2" />
                  Naar startpagina
                </Button>
              </div>

              <div className="text-center">
                <Button 
                  onClick={this.handleReportError} 
                  variant="ghost" 
                  size="sm" 
                  className="text-gray-500 hover:text-gray-700"
                >
                  <Bug className="h-4 w-4 mr-2" />
                  Rapporteer dit probleem
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
} 